import { useEffect, useState } from "react";
import { toHTML } from "@portabletext/to-html";
import { HELP_ARTICLES } from "../data/helpArticles";
import { API_BASE as API_BASE_URL } from "../config/apiBase";

const slugify = (value) =>
    String(value || "")
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

const hasApiConfig = Boolean(String(API_BASE_URL || "").trim());

const joinUrl = (base, path) => {
    const b = String(base || "").replace(/\/+$/, "");
    const p = String(path || "").replace(/^\/+/, "");
    return `${b}/${p}`;
};

const HTML_TAG_PATTERN = /<\/?[a-z][\s\S]*>/i;

const toRichTextHtml = (value) => {
    if (!value) return "";
    if (Array.isArray(value)) return portableTextToHtml(value);
    if (HTML_TAG_PATTERN.test(value)) return value;
    return markdownToHtml(String(value));
};

const normalizeHelpRecord = (record) => {
    const title = record?.title ?? "";
    const slug =
        record?.slug?.current ?? record?.slug ?? slugify(title) ?? "";
    const category = String(record?.category || "Other").trim() || "Other";
    const description =
        record?.lede ?? record?.intro ?? record?.description ?? "";
    const introHtml = toRichTextHtml(description);
    const rawContent = toRichTextHtml(
        record?.body ?? ""
    );
    const normalizedContent = ensureHeadingIds(rawContent);
    const path = record?.path ?? (slug ? `/help/${slug}` : "");
    const estimatedReadTime =
        record?.estimatedReadTime ?? record?.readTime ?? null;
    const lastModified = record?._updatedAt;
    const computedReadTime =
        estimatedReadTime ??
        (Array.isArray(record?.body) ? estimateReadTime(record.body) : null);

    const normalizeLink = (value) => {
        if (!value) return "";
        const raw = String(value).trim();
        if (!raw) return "";
        if (
            raw.startsWith("http://") ||
            raw.startsWith("https://") ||
            raw.startsWith("mailto:") ||
            raw.startsWith("/")
        ) {
            return raw;
        }
        if (raw.startsWith("help/")) return `/${raw}`;
        return `/${raw}`;
    };

    const normalizeCta = (cta) => {
        if (!cta || typeof cta !== "object") return null;
        const label = cta.label ?? cta.title ?? "";
        const href = normalizeLink(cta.href ?? cta.url ?? cta.path ?? "");
        if (!label || !href) return null;
        return { label: String(label), href };
    };

    const normalizeRelated = (value) => {
        if (!Array.isArray(value)) return null;
        const items = value
            .map((r) => {
                if (!r) return null;
                if (typeof r === "string") {
                    const href = normalizeLink(r);
                    return href ? { title: href, href } : null;
                }
                const title = r.title ?? r.name ?? "";
                const description = r.description ?? r.lede ?? r.subtitle ?? "";
                const href = normalizeLink(
                    r.href ??
                        r.path ??
                        r.url ??
                        (typeof r.slug === "string"
                            ? `/help/${r.slug}`
                            : r.slug?.current
                              ? `/help/${r.slug.current}`
                              : "")
                );
                if (!title || !href) return null;
                return { title: String(title), description, href };
            })
            .filter(Boolean);
        return items.length > 0 ? items : null;
    };

    return {
        id: record?.id ?? record?._id ?? slug ?? title,
        title,
        slug,
        description,
        intro: description,
        introHtml,
        content: normalizedContent.html,
        body: normalizedContent.html,
        path,
        category,
        updatedAt:
            record?.updatedAt ??
            record?.publishedAt ??
            record?._updatedAt ??
            null,
        primaryCta: normalizeCta(record?.primaryCta),
        secondaryCta: normalizeCta(record?.secondaryCta),
        sections: record?.sections ?? normalizedContent.sections,
        related: normalizeRelated(record?.related),
        estimatedReadTime: computedReadTime,
        lastModified,
    };
};

const normalizeHelpSource = (source) => {
    if (Array.isArray(source)) {
        return source.map(normalizeHelpRecord);
    }

    if (source?.records && Array.isArray(source.records)) {
        return source.records.map(normalizeHelpRecord);
    }

    if (source && typeof source === "object") {
        return Object.values(source).map(normalizeHelpRecord);
    }

    return [];
};

const filterVisible = (list) =>
    list.filter((item) => item.title && item.path && item.category);

const getFallbackHelpList = () => normalizeHelpSource(HELP_ARTICLES);

const LIST_CACHE_TTL_MS = 5 * 60 * 1000;
const DETAIL_CACHE_TTL_MS = 5 * 60 * 1000;
const helpListCache = { data: null, fetchedAt: 0 };
const helpDetailCache = new Map();

const isCacheFresh = (entry, ttlMs) =>
    entry?.fetchedAt && Date.now() - entry.fetchedAt < ttlMs;

const fetchHelpList = async () => {
    if (!hasApiConfig) return [];
    const res = await fetch(joinUrl(API_BASE_URL, "/help/articles"));
    if (!res.ok) throw new Error("Failed to load help articles.");
    return res.json();
};

const fetchHelpArticleBySlug = async (slug) => {
    if (!hasApiConfig || !slug) return null;
    const res = await fetch(
        joinUrl(API_BASE_URL, `/help/articles/${encodeURIComponent(slug)}`)
    );
    if (res.status === 404) return null;
    if (!res.ok) throw new Error("Failed to load help article.");
    return res.json();
};

export const useHelpArticles = () => {
    const [articles, setArticles] = useState(() => getFallbackHelpList());
    const [loading, setLoading] = useState(Boolean(hasApiConfig));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!hasApiConfig) {
            setLoading(false);
            return;
        }

        let active = true;

        const load = async () => {
            try {
                if (isCacheFresh(helpListCache, LIST_CACHE_TTL_MS)) {
                    setArticles(helpListCache.data || []);
                    setLoading(false);
                    return;
                }

                const records = await fetchHelpList();
                if (!active) return;
                const normalized = normalizeHelpSource(records);
                const visible = filterVisible(normalized);
                const next =
                    visible.length > 0 ? visible : getFallbackHelpList();
                helpListCache.data = next;
                helpListCache.fetchedAt = Date.now();
                setArticles(next);
            } catch (err) {
                if (!active) return;
                setError(err);
                setArticles(getFallbackHelpList());
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        return () => {
            active = false;
        };
    }, []);

    return { articles, loading, error, hasApiConfig };
};

export const useHelpArticle = (slug) => {
    const [article, setArticle] = useState(() => {
        const local = HELP_ARTICLES?.[slug];
        return local ? normalizeHelpRecord(local) : null;
    });
    const [loading, setLoading] = useState(Boolean(hasApiConfig));
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!hasApiConfig || !slug) {
            setLoading(false);
            return;
        }

        let active = true;

        const load = async () => {
            try {
                // Ensure UI stays in sync with the URL immediately on slug change.
                const local = HELP_ARTICLES?.[slug];
                setArticle(local ? normalizeHelpRecord(local) : null);

                const cached = helpDetailCache.get(slug);
                if (isCacheFresh(cached, DETAIL_CACHE_TTL_MS)) {
                    setArticle(cached.data || null);
                    setLoading(false);
                    return;
                }

                const record = await fetchHelpArticleBySlug(slug);
                if (!active) return;
                const normalized = record ? normalizeHelpRecord(record) : null;
                const visible =
                    normalized && filterVisible([normalized]).length > 0
                        ? normalized
                        : HELP_ARTICLES?.[slug]
                          ? normalizeHelpRecord(HELP_ARTICLES[slug])
                          : null;
                helpDetailCache.set(slug, {
                    data: visible,
                    fetchedAt: Date.now(),
                });
                setArticle(visible);
            } catch (err) {
                if (!active) return;
                setError(err);
                const local = HELP_ARTICLES?.[slug];
                setArticle(local ? normalizeHelpRecord(local) : null);
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        return () => {
            active = false;
        };
    }, [slug]);

    return { article, loading, error, hasApiConfig };
};

const portableTextToHtml = (blocks) => {
    try {
        return toHTML(blocks, {
            components: {
                types: {
                    image: ({ value }) => {
                        const url =
                            value?.asset?.url ||
                            value?.assetUrl ||
                            value?.url ||
                            null;
                        if (!url) return "";
                        const alt = value?.alt ? escapeHtml(value.alt) : "";
                        return `<figure class="article-image"><img src="${url}" alt="${alt}" loading="lazy" /></figure>`;
                    },
                },
                marks: {
                    link: ({ children, value }) => {
                        const href = value?.href;
                        if (!href) return children;
                        const safeHref = escapeHtml(String(href));
                        const rel = href.startsWith("http")
                            ? ' rel="noreferrer"'
                            : "";
                        const target = href.startsWith("http")
                            ? ' target="_blank"'
                            : "";
                        return `<a href="${safeHref}"${target}${rel}>${children}</a>`;
                    },
                },
            },
        });
    } catch (error) {
        return "";
    }
};

const estimateReadTime = (blocks) => {
    const text = portableTextToPlainText(blocks);
    const words = text.split(/\s+/).filter(Boolean).length;
    return Math.max(1, Math.round(words / 200));
};

const portableTextToPlainText = (blocks) => {
    if (!Array.isArray(blocks)) return "";
    const parts = [];
    blocks.forEach((block) => {
        if (block?._type === "block" && Array.isArray(block.children)) {
            block.children.forEach((child) => {
                if (typeof child?.text === "string") {
                    parts.push(child.text);
                }
            });
        }
    });
    return parts.join(" ");
};

const markdownToHtml = (input) => {
    const text = escapeHtml(input).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const lines = text.split("\n");
    const blocks = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];
        if (!line.trim()) {
            i += 1;
            continue;
        }

        const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            const level = headingMatch[1].length;
            blocks.push(
                `<h${level}>${formatInlineMarkdown(headingMatch[2])}</h${level}>`
            );
            i += 1;
            continue;
        }

        if (/^[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
                const item = lines[i].replace(/^[-*]\s+/, "");
                items.push(`<li>${formatInlineMarkdown(item)}</li>`);
                i += 1;
            }
            blocks.push(`<ul>${items.join("")}</ul>`);
            continue;
        }

        if (/^\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
                const item = lines[i].replace(/^\d+\.\s+/, "");
                items.push(`<li>${formatInlineMarkdown(item)}</li>`);
                i += 1;
            }
            blocks.push(`<ol>${items.join("")}</ol>`);
            continue;
        }

        const paragraphLines = [];
        while (
            i < lines.length &&
            lines[i].trim() &&
            !/^(#{1,6})\s+/.test(lines[i]) &&
            !/^[-*]\s+/.test(lines[i]) &&
            !/^\d+\.\s+/.test(lines[i])
        ) {
            paragraphLines.push(lines[i]);
            i += 1;
        }

        blocks.push(`<p>${formatInlineMarkdown(paragraphLines.join(" "))}</p>`);
    }

    return blocks.join("\n");
};

const formatInlineMarkdown = (value) => {
    let result = value;
    result = result.replace(
        /!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g,
        '<img src="$2" alt="$1" />'
    );
    result = result.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
    result = result.replace(/__(.+?)__/g, "<strong>$1</strong>");
    result = result.replace(/\*(.+?)\*/g, "<em>$1</em>");
    result = result.replace(/_(.+?)_/g, "<em>$1</em>");
    result = result.replace(/`(.+?)`/g, "<code>$1</code>");
    result = result.replace(
        /\[(.+?)\]\((https?:\/\/[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
    );
    return result;
};

const escapeHtml = (value) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const stripHtml = (html) => {
    if (!html) return "";
    return html
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
};

const ensureHeadingIds = (html) => {
    if (!html) return { html: "", sections: [] };

    const sections = [];
    const idCounts = new Map();

    const withIds = html.replace(
        /<h([2-3])([^>]*)>([\s\S]*?)<\/h\1>/gi,
        (match, level, attrs, inner) => {
            const text = stripHtml(inner);
            if (!text) return match;
            const existingIdMatch = attrs.match(/\sid\s*=\s*["']([^"']+)["']/i);
            let id = existingIdMatch ? existingIdMatch[1] : slugify(text);
            if (!id) return match;
            const count = idCounts.get(id) ?? 0;
            idCounts.set(id, count + 1);
            if (count > 0) {
                id = `${id}-${count + 1}`;
            }
            const nextAttrs = existingIdMatch ? attrs : `${attrs} id="${id}"`;
            sections.push({ id, title: text });
            return `<h${level}${nextAttrs}>${inner}</h${level}>`;
        }
    );

    return { html: withIds, sections };
};

// Airtable attachment rendering removed; Sanity images are handled via Portable Text serializers.
